import { MouseEventHandler, ReactNode, useMemo } from 'react';
import { useLocation } from 'react-router';

import { FCWithChildren } from 'common/types';
import { IconName } from 'components/_core/Icon';
import SunshineIcon from 'components/_core/SunshineIcon';

import NavSubMenuItem from './components/NavSubMenuItem';
import * as S from './styles';

export interface Link {
  children?: ReactNode;
  disabled?: boolean;
  name: string;
  to: string;
}

interface NavMenuCollapseProps {
  onClick?: MouseEventHandler<HTMLElement>;
  disabled?: boolean;
  links: Link[];
  icon: IconName;
  toMainRoute: string;
  mainRouteName: string;
  /**
   * Wording of the Cash flow main menu and its new feature icon are too long to fit with `space-8` left-margin of the chevron icon
   * We reduce slightly the margin between the chevron icon and the new feature icon for the time the new feature icon is here.
   * @todo remove this prop once new feature icon is removed from `Suivi des finances` menu.
   * @see https://linear.app/shine/issue/CPA-1263/remove-hasnewfeatureicon-prop-from-navmenucollapse-once-new-feature
   */
  hasNewFeatureIcon?: boolean;
}

const NavMenuCollapse: FCWithChildren<NavMenuCollapseProps> = ({
  children,
  disabled,
  hasNewFeatureIcon,
  icon,
  links,
  mainRouteName,
  onClick,
  toMainRoute,
}) => {
  const { pathname } = useLocation();

  // Force for a route to select the corresponding MainNavMenuItem
  const isActive = useMemo(
    () =>
      pathname.includes(toMainRoute) ||
      links.some(
        (link) =>
          link.to === location.pathname || location.pathname.includes(link.to),
      ),
    [pathname, toMainRoute, links],
  );

  const hasSubRoutes = pathname.split('/').length >= 3 || isActive;

  return (
    <>
      <S.MainNavMenuItem
        $disableLabelUnderline={hasSubRoutes}
        disabled={disabled}
        icon={icon}
        isActive={() => isActive}
        label={mainRouteName}
        onClick={onClick}
        to={toMainRoute}
      >
        {children}

        <SunshineIcon
          className="chevron-down-icon"
          marginLeft={hasNewFeatureIcon ? 'space-4' : 'space-8'}
          name="chevron-down"
        />
      </S.MainNavMenuItem>

      <S.Collapse>
        {links.map(({ name, ...props }) => (
          <NavSubMenuItem key={name} label={name} {...props} />
        ))}
      </S.Collapse>
    </>
  );
};

export default NavMenuCollapse;
